import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Microphone recording for voice notes (docs/voiceFeature.md, UC-A):
 * getUserMedia + MediaRecorder, input level, wall-clock elapsed time, pause,
 * a hard limit (3 min) with a warning shortly before, and a low-signal hint
 * (pumps louder than the voice). Never uploads anything itself — the finished
 * recording goes to `onRecorded`.
 */

export type VoiceRecorderStatus =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'paused'
  | 'denied'
  | 'unsupported'
  | 'error';

export interface RecordedMeta {
  durationMs: number;
  /** ISO time the recording started (kept for offline notes). */
  recordedAt: string;
  mimeType: string;
}

export interface UseVoiceRecorderOptions {
  onRecorded: (blob: Blob, meta: RecordedMeta) => void;
  maxDurationSec?: number;
  /** The limit warning shows this many seconds before the end. */
  warnBeforeSec?: number;
  /** Level (0..1 RMS × gain) below which the input counts as too quiet. */
  lowSignalThreshold?: number;
  lowSignalMs?: number;
}

export interface UseVoiceRecorderResult {
  status: VoiceRecorderStatus;
  isSupported: boolean;
  elapsedMs: number;
  level: number;
  lowSignal: boolean;
  limitWarning: boolean;
  /** Asks for the microphone and starts. Resolves false when that was not possible. */
  start: () => Promise<boolean>;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  /** Stops without calling onRecorded. */
  discard: () => void;
}

/** Preferred container order: Opus in WebM (Chrome/Firefox), then MP4/AAC (Safari). */
export const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'] as const;

export function isVoiceRecordingSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return '';
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}

const TICK_MS = 250;
const LEVEL_INTERVAL_MS = 100;
const LEVEL_GAIN = 4;

interface Session {
  stream: MediaStream;
  recorder: MediaRecorder;
  chunks: Blob[];
  recordedAt: Date;
  /** Active milliseconds before the current running stretch. */
  accumulatedMs: number;
  /** Wall-clock start of the current running stretch; null while paused. */
  runningSince: number | null;
  discarded: boolean;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  lastLoudAt: number;
}

function activeMs(session: Session, now: number): number {
  return session.accumulatedMs + (session.runningSince === null ? 0 : now - session.runningSince);
}

export function useVoiceRecorder({
  onRecorded,
  maxDurationSec = 180,
  warnBeforeSec = 10,
  lowSignalThreshold = 0.04,
  lowSignalMs = 3000,
}: UseVoiceRecorderOptions): UseVoiceRecorderResult {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [lowSignal, setLowSignal] = useState(false);
  const sessionRef = useRef<Session | null>(null);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = useRef<number | null>(null);
  const isSupported = isVoiceRecordingSupported();

  const cleanupTimers = useCallback(() => {
    if (tickRef.current !== null) clearInterval(tickRef.current);
    tickRef.current = null;
    if (rafRef.current !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const releaseMedia = useCallback((session: Session) => {
    session.stream.getTracks().forEach((track) => track.stop());
    if (session.audioContext && session.audioContext.state !== 'closed') {
      void session.audioContext.close().catch(() => undefined);
    }
  }, []);

  const finish = useCallback(
    (discard: boolean) => {
      const session = sessionRef.current;
      if (!session) return;
      session.discarded = discard;
      if (session.runningSince !== null) {
        session.accumulatedMs = activeMs(session, Date.now());
        session.runningSince = null;
      }
      cleanupTimers();
      if (session.recorder.state !== 'inactive') {
        session.recorder.stop();
      } else {
        releaseMedia(session);
        sessionRef.current = null;
        setStatus('idle');
      }
    },
    [cleanupTimers, releaseMedia],
  );

  const stop = useCallback(() => finish(false), [finish]);
  const discard = useCallback(() => finish(true), [finish]);

  const sampleLevel = useCallback(() => {
    let lastSample = 0;
    const buffer = new Uint8Array(1024);
    const loop = (): void => {
      const session = sessionRef.current;
      if (!session || !session.analyser) return;
      const now = Date.now();
      if (now - lastSample >= LEVEL_INTERVAL_MS && session.runningSince !== null) {
        lastSample = now;
        session.analyser.getByteTimeDomainData(buffer);
        let sum = 0;
        for (const value of buffer) {
          const centred = (value - 128) / 128;
          sum += centred * centred;
        }
        const rms = Math.min(1, Math.sqrt(sum / buffer.length) * LEVEL_GAIN);
        setLevel(rms);
        if (rms >= lowSignalThreshold) session.lastLoudAt = now;
        setLowSignal(now - session.lastLoudAt >= lowSignalMs);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    if (typeof requestAnimationFrame === 'function') rafRef.current = requestAnimationFrame(loop);
  }, [lowSignalMs, lowSignalThreshold]);

  const start = useCallback(async (): Promise<boolean> => {
    if (sessionRef.current) return false;
    if (!isVoiceRecordingSupported()) {
      setStatus('unsupported');
      return false;
    }
    setStatus('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      setStatus(['NotAllowedError', 'SecurityError', 'NotFoundError', 'NotReadableError'].includes(name) ? 'denied' : 'error');
      return false;
    }

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      setStatus('unsupported');
      return false;
    }

    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        audioContext = new Ctx();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        audioContext.createMediaStreamSource(stream).connect(analyser);
      }
    } catch {
      audioContext = null;
      analyser = null;
    }

    const now = Date.now();
    const session: Session = {
      stream,
      recorder,
      chunks: [],
      recordedAt: new Date(now),
      accumulatedMs: 0,
      runningSince: now,
      discarded: false,
      audioContext,
      analyser,
      lastLoudAt: now,
    };
    sessionRef.current = session;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) session.chunks.push(event.data);
    };
    recorder.onstop = () => {
      releaseMedia(session);
      cleanupTimers();
      if (sessionRef.current === session) sessionRef.current = null;
      setStatus('idle');
      setLevel(0);
      setLowSignal(false);
      if (session.discarded) return;
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(session.chunks, { type });
      onRecordedRef.current(blob, {
        durationMs: Math.round(session.accumulatedMs),
        recordedAt: session.recordedAt.toISOString(),
        mimeType: type,
      });
    };

    recorder.start(1000);
    setElapsedMs(0);
    setLevel(0);
    setLowSignal(false);
    setStatus('recording');

    tickRef.current = setInterval(() => {
      const current = sessionRef.current;
      if (!current) return;
      const elapsed = activeMs(current, Date.now());
      setElapsedMs(elapsed);
      if (elapsed >= maxDurationSec * 1000) finish(false);
    }, TICK_MS);
    sampleLevel();
    return true;
  }, [cleanupTimers, finish, maxDurationSec, releaseMedia, sampleLevel]);

  const pause = useCallback(() => {
    const session = sessionRef.current;
    if (!session || session.recorder.state !== 'recording') return;
    session.recorder.pause();
    session.accumulatedMs = activeMs(session, Date.now());
    session.runningSince = null;
    setElapsedMs(session.accumulatedMs);
    setLevel(0);
    setStatus('paused');
  }, []);

  const resume = useCallback(() => {
    const session = sessionRef.current;
    if (!session || session.recorder.state !== 'paused') return;
    session.recorder.resume();
    session.runningSince = Date.now();
    session.lastLoudAt = Date.now();
    setStatus('recording');
  }, []);

  // Unmount: stop and drop whatever is running (the microphone must never stay on).
  useEffect(
    () => () => {
      const session = sessionRef.current;
      if (!session) return;
      session.discarded = true;
      cleanupTimers();
      try {
        if (session.recorder.state !== 'inactive') session.recorder.stop();
      } catch {
        // already stopped
      }
      releaseMedia(session);
      sessionRef.current = null;
    },
    [cleanupTimers, releaseMedia],
  );

  const limitWarning =
    (status === 'recording' || status === 'paused') && elapsedMs >= (maxDurationSec - warnBeforeSec) * 1000;

  return { status, isSupported, elapsedMs, level, lowSignal, limitWarning, start, stop, pause, resume, discard };
}
